import React, { createContext, useContext, useReducer, useEffect } from 'react';
import type { AuthState, LoginRequest, User, UserRole, Permission } from '../types/auth';
import { authService } from '../services';
import { canAccessRoute } from '../config/roles';
import { validateUserRole } from '../utils/roleValidation';

interface AuthContextType extends AuthState {
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  hasPermission: (permission: Permission) => boolean;
  hasRole: (roles: UserRole | UserRole[]) => boolean;
  canAccessRoute: (routePath: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'SET_USER'; payload: User }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'UPDATE_PERMISSIONS'; payload: Permission[] };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, isLoading: true };

    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        role: action.payload.user.role,
        permissions: action.payload.user.permissions,
        isAuthenticated: true,
        isLoading: false,
      };

    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        role: null,
        permissions: [],
        isAuthenticated: false,
        isLoading: false,
      };

    case 'LOGOUT':
      return {
        user: null,
        token: null,
        role: null,
        permissions: [],
        isAuthenticated: false,
        isLoading: false,
      };

    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        role: action.payload.role,
        permissions: action.payload.permissions,
        isAuthenticated: true,
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    case 'UPDATE_PERMISSIONS':
      return {
        ...state,
        permissions: action.payload,
        user: state.user ? { ...state.user, permissions: action.payload } : null,
      };

    default:
      return state;
  }
};

const initialState: AuthState = {
  user: null,
  token: null,
  role: null,
  permissions: [],
  isLoading: true,
  isAuthenticated: false,
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const login = async (credentials: LoginRequest) => {
    try {
      dispatch({ type: 'LOGIN_START' });

      const response = await authService.login(credentials);

      
      // Crear objeto User con datos del backend
      const roleName = response.role?.role_name || 'user';
      const validRole = validateUserRole(roleName);
      
      // IMPORTANTE: Guardar el token PRIMERO antes de hacer llamadas adicionales
      localStorage.setItem('token', response.token);

      // Obtener permisos reales del backend
      let permissions: Permission[] = [];

      
      if (response.role?.id) {
        try {
          // Pequeño delay para asegurar que el token esté disponible
          await new Promise(resolve => setTimeout(resolve, 100));
          
          const permissionsResponse = await authService.getUserRole(response.role.id);
          permissions = permissionsResponse.permissions;
        } catch (permError) {
          
          // Si es error de sesión expirada, intentar una vez más después de un delay
          if (permError instanceof Error && permError.message.includes('Sesión expirada')) {
            try {
              await new Promise(resolve => setTimeout(resolve, 500));
              const retryResponse = await authService.getUserRole(response.role.id);
              permissions = retryResponse.permissions;
            } catch (retryError) {
              permissions = ['dashboard.view'];
            }
          } else {
            // Si falla, usar permisos mínimos solo para permitir login
            permissions = ['dashboard.view'];
          }
        }
      } else {
        // Si no hay rol, usar permisos mínimos
        permissions = ['dashboard.view'];
      }
      
      const user: User = {
        id: response.id.toString(),
        name: response.full_name,
        email: response.email,
        role: validRole,
        status: 'active',
        permissions: permissions, // Usar permisos reales del backend
        roleId: response.roleUser || response.role?.id || 1,
        roleName: roleName
      };

      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('id', user.id);
      localStorage.setItem('userRole', user.role);
      localStorage.setItem('userPermissions', JSON.stringify(user.permissions));
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user,
          token: response.token,
        },
      });
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE' });
      throw error;
    }
  };

  const logout = async () => {
    // Limpiar localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userPermissions');
    localStorage.removeItem('email');
    
    await authService.logout();
    dispatch({ type: 'LOGOUT' });
  };

  const checkAuth = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      const token = localStorage.getItem('token');
      const savedUserStr = localStorage.getItem('user');
      const savedRole = localStorage.getItem('userRole');
      const savedPermissionsStr = localStorage.getItem('userPermissions');

      if (!token || !savedUserStr || !savedRole || !savedPermissionsStr) {
        dispatch({ type: 'SET_LOADING', payload: false });
        return; 
      }

      try {
        // Verificar si el token sigue siendo válido y obtener datos actuales
        const currentUser = await authService.getCurrentUser();
        
        // Usar datos del backend si están disponibles, sino usar datos guardados
        const savedUser = savedUserStr ? JSON.parse(savedUserStr) : null;
        const savedPermissions = savedPermissionsStr ? JSON.parse(savedPermissionsStr) : [];
        
        // Determinar el rol y usar permisos reales del backend
        const userRole = currentUser.role || validateUserRole(savedRole) || 'admin';
        let userPermissions = currentUser.permissions && currentUser.permissions.length > 0 
          ? currentUser.permissions 
          : savedPermissions && savedPermissions.length > 0 
            ? savedPermissions 
            : [];
        
        // Si no hay permisos, intentar obtenerlos del backend
        if (userPermissions.length === 0 && currentUser.roleId) {
          try {
            const permissionsResponse = await authService.getUserRole(currentUser.roleId);
            userPermissions = permissionsResponse.permissions;

          } catch (permError) {
            // Solo usar permisos mínimos si es absolutamente necesario
            userPermissions = ['dashboard.view'];
          }
        }
        
        const user: User = {
          id: currentUser.id || savedUser?.id || '1',
          name: currentUser.name || savedUser?.name || 'Usuario',
          email: currentUser.email || savedUser?.email || 'user@example.com',
          role: userRole,
          status: currentUser.status || savedUser?.status || 'active',
          permissions: userPermissions,
          roleId: currentUser.roleId || savedUser?.roleId || 1,
          roleName: currentUser.roleName || savedUser?.roleName || 'admin'
        };

        // Actualizar localStorage con datos actualizados
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('userRole', user.role);
        localStorage.setItem('userPermissions', JSON.stringify(user.permissions));

        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: {
            user,
            token,
          },
        });

      } catch (parseError) {
        // Si hay error al parsear, usar datos básicos
        const fallbackRole = savedRole || 'admin';
        const validFallbackRole = validateUserRole(fallbackRole);
          
        const basicUser: User = {
          id: '1',
          name: savedUserStr,
          email: 'user@example.com',
          role: validFallbackRole,
          status: 'active',
          permissions: savedPermissionsStr ? JSON.parse(savedPermissionsStr) : [],
          roleId: 1,
          roleName: fallbackRole
        };

        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: {
            user: basicUser,
            token,
          },
        });
      }

      dispatch({ type: 'SET_LOADING', payload: false });

    } catch (error) {
      // Limpiar datos corruptos
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userPermissions');
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Funciones auxiliares para verificar permisos y roles
  const hasPermission = (permission: Permission): boolean => {
    if (!state.user) return false;
    return state.permissions.includes(permission);
  };

  const hasRole = (roles: UserRole | UserRole[]): boolean => {
    if (!state.user) return false;
    const rolesArray = Array.isArray(roles) ? roles : [roles];
    return rolesArray.includes(state.user.role);
  };

  const checkRouteAccess = (routePath: string): boolean => {
    if (!state.user) {
      return false
    }
    
    
    const hasAccess = canAccessRoute(state.user.role, routePath); 
    return hasAccess;
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        checkAuth,
        hasPermission,
        hasRole,
        canAccessRoute: checkRouteAccess,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};